import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';

import { getUnderlineType, UnderlineType } from '../tools';
import { validateState, VALIDATION_EMPTY_FIELD } from '../../../hooks/useForm/validate';
import { useValidateState } from '../../../hooks/useValidateState';
import { checkExistValue } from '../../../../service/utils';
import { InputStyle } from './TotalPanelField';
import { useStyles } from './useStyles';

export const useTotalPanelField = (
    name,
    onEdit,
    onFocus,
    label,
    style,
    value1,
    value2,
    isTotalEditable,
    isDisable,
    required,
    validator,
    defaultValue,
    helperText
) => {
    const { t: translate } = useTranslation();
    const classes = useStyles();
    const [errorText, changeError] = useValidateState(validator, defaultValue, required, translate);
    const [value, changeValue] = useState(defaultValue);

    let customHelperText = helperText === translate(VALIDATION_EMPTY_FIELD) ? '' : helperText;

    useEffect(() => {
        if (checkExistValue(defaultValue)) {
            changeValue(defaultValue);
            changeError((validateState(validator, defaultValue, required, translate) || {}).errorText);
        }

        if (helperText) {
            changeError(helperText);
        }
    }, [defaultValue, helperText, translate]);

    const handleEdit = useCallback(
        (evt) => {
            const fieldName = evt.target.name;
            const fieldValue = evt.currentTarget.value === '' ? undefined : +evt.currentTarget.value;
            const validateRes = validateState({ ...validator, isRequired: required }, fieldValue, false, translate);

            if (validateRes === null) {
                onEdit(fieldName, fieldValue);
            } else {
                changeError(validateRes.errorText);
            }
        },
        [validator, required, translate]
    );

    const handleChange = useCallback((event) => changeValue(event.target.value), []);

    const placeholder = useMemo(() => {
        if (name === 'underload') {
            return '0';
        }

        return label;
    }, [name, label]);

    const fieldStyle = useMemo(() => {
        if (style === InputStyle.SIMPLE) {
            return isTotalEditable ? classes.editedField : classes.disabledTextFieldStyle;
        }

        const wellDone = isDisable ? classes.wellDone : classes.wellDoneEdited;
        const badWork = isDisable ? classes.badWork : classes.badWorkEdited;
        let inputClassName = classnames(defaultClassName, badWork);
        const defaultClassName = isTotalEditable ? classes.editedField : classes.disabledTextFieldStyle;
        const underlineType = getUnderlineType(value1, value2);

        if (!value1 || !value2 || value1 - value2 === 0) {
            inputClassName = defaultClassName;
        } else if (underlineType === UnderlineType.WELL_DONE) {
            inputClassName = classnames(defaultClassName, wellDone);
        }

        return style === InputStyle.SPECIAL &&
            ['payLoadReal', 'zeroFuelWeightReal', 'takeOffWeightReal', 'landingWeightReal'].indexOf(name) !== -1
            ? inputClassName
            : '';
    }, [style, name, isDisable, value1, value2, isTotalEditable]);

    const handleInputFocus = useCallback((e) => {
        changeError('');

        if (onFocus) {
            onFocus(e);
        }
    }, []);

    return {
        customHelperText,
        fieldStyle,
        errorText,
        placeholder,
        value,
        handleEdit,
        handleChange,
        handleInputFocus,
    };
};
